import { ServiceVariation } from "@/services/backend/omni/servicevariation/types/service-variation";
import { UUID } from "@bitory-gmbh/nidavellir";
import { backendApi } from "@/services/backend/api";
import { base64ListRequest } from "@/services/backend/_lib/base64-list-request";
import {
  providesEntity,
  providesList,
} from "@/services/backend/_lib/tag-helpers";
import { updateCache } from "@/services/backend/_lib/update-cache";
import { Filter } from "@/shared/lib/filter/filter";
import { Lst } from "@/shared/lib/lst/lst";
import { OrdrBy } from "@/shared/lib/ordr/ordrBy";
import { PositionsBaseData, PositionsBaseDataPatch } from "../types/base-data";

/**
 * A save refetches the entry either way: on error to restore the server
 * state over the optimistic value, on success to pick up what the server
 * derived from it. See the create-auto-saving-input convention.
 */
const invalidatesPositionsBaseData = (
  _result: unknown,
  _error: unknown,
  request: { id: UUID },
) =>
  [
    { type: "OmniPositionsBaseData" as const, id: request.id },
    { type: "OmniPositionsBaseData" as const, id: "LIST" },
  ] as const;

export const omniPositionsBaseDataApi = backendApi
  .enhanceEndpoints({
    addTagTypes: ["OmniPositionsBaseData", "DefaultBaseData"],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      omniPositionsBaseDataCreate: build.mutation<
        PositionsBaseData,
        CreateRequest
      >({
        query: (request) => ({
          url: "/omni/positions/base-data",
          method: "POST",
          body: request,
        }),
        invalidatesTags: [{ type: "OmniPositionsBaseData", id: "LIST" }],
      }),
      omniPositionsBaseDataShow: build.query<PositionsBaseData, ShowRequest>({
        query: (request) => ({
          url: "/omni/positions/base-data",
          method: "GET",
          params: request,
        }),
        providesTags: providesEntity("OmniPositionsBaseData"),
      }),
      omniPositionsBaseDataShowFor: build.query<
        PositionsBaseData,
        ShowForRequest
      >({
        query: (request) => ({
          url: "/omni/positions/base-data/for",
          method: "GET",
          params: request,
        }),
        // The result depends on the service variations of all instances and on the
        // default, so it is refetched when either changes.
        providesTags: [
          { type: "OmniPositionsBaseData", id: "LIST" },
          "DefaultBaseData",
        ],
      }),
      omniPositionsBaseDataList: build.query<
        Lst<PositionsBaseData>,
        ListRequest
      >({
        query: (request) => ({
          url: "/omni/positions/base-data/list",
          method: "GET",
          params: base64ListRequest(request),
        }),
        providesTags: (result) =>
          providesList(result?.items, "OmniPositionsBaseData"),
      }),
      omniPositionsBaseDataPatch: build.mutation<
        PositionsBaseData,
        PatchRequest
      >({
        query: (request) => ({
          url: "/omni/positions/base-data",
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: invalidatesPositionsBaseData,
      }),
      omniPositionsBaseDataDelete: build.mutation<null, DeleteRequest>({
        query: (request) => ({
          url: "/omni/positions/base-data",
          method: "DELETE",
          body: request,
        }),
        // The backend refuses the delete while the instance is a
        // default, so the defaults are refetched either way.
        invalidatesTags: [
          { type: "OmniPositionsBaseData", id: "LIST" },
          "DefaultBaseData",
        ],
      }),
    }),
  });

export const {
  useOmniPositionsBaseDataCreateMutation,
  useOmniPositionsBaseDataShowQuery,
  useOmniPositionsBaseDataShowForQuery,
  useOmniPositionsBaseDataListQuery,
  useOmniPositionsBaseDataPatchMutation,
  useOmniPositionsBaseDataDeleteMutation,
} = omniPositionsBaseDataApi;

export interface CreateRequest {
  id: UUID;
  companyId: UUID;
  serviceKind: string;
  serviceVariation: ServiceVariation;
  label: string;
}

export interface ShowRequest {
  id: UUID;
}

export interface ShowForRequest {
  companyId: UUID;
  serviceKind: string;
  serviceVariation?: ServiceVariation;
}

export interface ListRequest {
  /** Required by the backend: active, with at least one company. */
  company: Filter<UUID>;
  serviceKind: Filter<string>;
  serviceVariation?: Filter<string>;
  label?: Filter<string>;
  orderBy?: OrdrBy<"id" | "label" | "createdAt" | "updatedAt">[];
  limit?: number;
  offset?: number;
}

export type PatchRequest = PositionsBaseDataPatch & { id: UUID };

export interface DeleteRequest {
  id: UUID;
}

export const updateOmniPositionsBaseDataCache = updateCache(
  omniPositionsBaseDataApi,
  "omniPositionsBaseDataShow",
);
